import { useState } from 'react'
import { Check, History, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import type { AgentThread } from '@/lib/api'
import type { ScopeKind } from '@/types/scope'

interface ThreadPickerProps {
  scopeKind: ScopeKind
  threads: AgentThread[]
  activeThreadId: string | null
  threadPreviewById: Record<string, string>
  disabled?: boolean
  onLoadThreads: () => Promise<void>
  onSwitchThread: (threadId: string) => Promise<void>
}

function getChatTitle(thread: AgentThread, threadPreviewById: Record<string, string>): string {
  const name = thread.name?.trim()
  if (name) return name
  const preview = threadPreviewById[thread.id]?.trim()
  if (preview) return preview
  return thread.carryMode === 'carry_summary' ? 'Carry-forward chat' : 'New chat'
}

function formatRelativeAgeShort(isoTimestamp: string, nowMs = Date.now()): string {
  const timestampMs = new Date(isoTimestamp).getTime()
  if (Number.isNaN(timestampMs)) return ''

  const deltaSeconds = Math.max(0, Math.floor((nowMs - timestampMs) / 1000))
  const minute = 60
  const hour = 60 * minute
  const day = 24 * hour
  const month = 30 * day
  const year = 365 * day

  if (deltaSeconds < hour) {
    const minutes = Math.max(1, Math.floor(deltaSeconds / minute))
    return `${minutes}m`
  }
  if (deltaSeconds < day) {
    const hours = Math.floor(deltaSeconds / hour)
    return `${hours}h`
  }
  if (deltaSeconds < month) {
    const days = Math.floor(deltaSeconds / day)
    return `${days}d`
  }
  if (deltaSeconds < year) {
    const months = Math.floor(deltaSeconds / month)
    return `${months}mo`
  }

  const years = Math.floor(deltaSeconds / year)
  return `${years}y`
}

export function ThreadPicker({
  scopeKind,
  threads,
  activeThreadId,
  threadPreviewById,
  disabled = false,
  onLoadThreads,
  onSwitchThread,
}: ThreadPickerProps) {
  const [open, setOpen] = useState(false)
  const [isLoadingList, setIsLoadingList] = useState(false)
  const [isWorking, setIsWorking] = useState(false)

  const handleOpenChange = async (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      return
    }

    setIsLoadingList(true)
    try {
      await onLoadThreads()
    } finally {
      setIsLoadingList(false)
    }
  }

  const handleSwitch = async (threadId: string) => {
    if (isWorking || disabled || threadId === activeThreadId) return

    setIsWorking(true)
    try {
      await onSwitchThread(threadId)
      setOpen(false)
    } finally {
      setIsWorking(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={(next) => { void handleOpenChange(next) }}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          aria-label="Open chat history"
          disabled={disabled || isWorking}
        >
          {isWorking ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-2">
        <div className="px-2 pt-1 pb-2 border-b">
          <p className="text-sm font-medium">Chats</p>
          <p className="text-[11px] text-muted-foreground">
            {scopeKind === 'unit' ? 'Section chat history' : 'Document chat history'}
          </p>
        </div>

        <div className="max-h-56 overflow-y-auto py-1 scrollbar-thin">
          {isLoadingList ? (
            <div className="flex items-center gap-2 px-2 py-3 text-meta">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading chats...
            </div>
          ) : threads.length === 0 ? (
            <div className="px-2 py-3 text-meta">
              No chats yet.
            </div>
          ) : (
            threads.map((thread) => {
              const isActive = thread.id === activeThreadId
              const chatTitle = getChatTitle(thread, threadPreviewById)
              const relativeTime = formatRelativeAgeShort(thread.updatedAt)
              const absoluteTimeLabel = new Date(thread.updatedAt).toLocaleString()
              return (
                <button
                  key={thread.id}
                  type="button"
                  className={cn(
                    'w-full text-left px-2 py-2 rounded-md transition-colors',
                    'hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                    isActive && 'bg-muted'
                  )}
                  onClick={() => { void handleSwitch(thread.id) }}
                  disabled={isWorking || disabled}
                >
                  <div className="flex items-start gap-2">
                    <span className="mt-0.5 shrink-0 text-primary">
                      {isActive ? <Check className="h-3.5 w-3.5" /> : <span className="block h-3.5 w-3.5" />}
                    </span>
                    <div className="min-w-0 flex items-center gap-2 w-full">
                      <p className="text-xs font-medium truncate flex-1">{chatTitle}</p>
                      {relativeTime && (
                        <span
                          className="text-[11px] text-muted-foreground shrink-0"
                          title={absoluteTimeLabel}
                          aria-label={`Updated ${absoluteTimeLabel}`}
                        >
                          {relativeTime}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              )
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
